import { faBadgeCheck } from "@fortawesome/pro-regular-svg-icons/faBadgeCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { ConvertMprcColorsToString } from 'components';
import { CardColor, EvolutionPermissions, IndicatorCardActionType, IndicatorCardDto } from "generated/CoreApi_v1";
import { useSnackbar } from 'notistack';
import React from 'react';
import { NavigateOptions, useNavigate } from 'react-router';
import { DashboardPaths } from "routes/DashboardRouter";
import { IntegrationPaths } from "routes/IntegrationRouter";
import { LmsPaths } from 'routes/LmsRouter';
import { OperationMode } from 'views/lms/devPlan/Home/Edit/EditDevPlanView/EditDevPlanView';

///////////////////////////////////////////////////////////////////////////////

interface NavigateProps {
    path: string;
    options?: NavigateOptions;
}

const discoverPath = (action: IndicatorCardActionType, data?: Record<string, any>): NavigateProps | undefined => {
    switch (action) {
        case IndicatorCardActionType.LmsCatalog:
            return { path: LmsPaths.StudentCatalog };

        case IndicatorCardActionType.LmsAttendingTrainings:
            return { path: LmsPaths.CarouselTrainingAttending };

        case IndicatorCardActionType.LmsMandatoryTrainings:
            return { path: LmsPaths.CarouselTrainingMandatory };

        case IndicatorCardActionType.KnowledgeChallenge:
            return { path: LmsPaths.NewKnowledgeTest };

        case IndicatorCardActionType.LmsMyDevelopmentPlan:
            return { path: LmsPaths.MyDevPlans };

        case IndicatorCardActionType.DashboardUserEvolutionChallenge:
            return { path: DashboardPaths.UserEvolution, options: { state: { tab: EvolutionPermissions.Challenge } } };

        case IndicatorCardActionType.LmsDevelopmentPlan:
            if (data != undefined && 'devPlanId' in data) {
                return { path: LmsPaths.BuildDevPlanView(data.devPlanId, OperationMode.View) };
            } else {
                return { path: LmsPaths.MyDevPlans };
            }

        case IndicatorCardActionType.PerformaGoals:
            return { path: IntegrationPaths.Goals };
            
        case IndicatorCardActionType.PerformaPerformanceEvaluation:
            return { path: IntegrationPaths.Performance };
    }

    return undefined;
};

///////////////////////////////////////////////////////////////////////////////

interface IIndicatorCardDtoActionsProps extends React.PropsWithChildren {
    item: IndicatorCardDto;
}

export const IndicatorCardAction = ({ item, children }: IIndicatorCardDtoActionsProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const handleSelection = () => {
        switch (item.action) {
            default:
                const pathResult = discoverPath(item.action, item.data);
                if (pathResult !== undefined) navigate(pathResult.path, pathResult.options);
                else enqueueSnackbar("Ação não implementada", { variant: "error" });
                break;
        }
    };

    return (
        <Card
            key={`${item.uniqueId}-indicator`}
            sx={{
                width: 326,
                height: 105,
                boxShadow: "rgba(0,0,0,0.1) 2px 2px 8px 2px",
                mt: "6px",
                mr: "8px",
            }}
            onClick={handleSelection}
            className={AppGlobalClassKeys.clickable}
            elevation={0}
        >
            {children}
        </Card>
    );
};

///////////////////////////////////////////////////////////////////////////////

const IndicatorCard = (item: IndicatorCardDto) => {
    return (
        <IndicatorCardAction item={item}>
            <Box
                height="100%"
                sx={
                    (theme) => {
                        const color = item.color ? ConvertMprcColorsToString(theme, CardColor[item.color].toCamelcase() as MprcColorsExtended) : undefined;
                        return {
                            borderLeftWidth: 12,
                            borderLeftStyle: "solid",
                            borderLeftColor: color,
                            color: color
                        };
                    }
                }
            >
                <Stack direction="row" justifyContent="flex-start" paddingTop={1} paddingBottom={1} height="calc(100% - 16px)" letterSpacing={0}>
                    <Typography fontSize="32px" fontWeight="bold" lineHeight="unset" component="div" color="inherit" sx={{ pl: 1, pr: 1, mt: 1 }}>
                        {item.value == "success" ? <FontAwesomeIcon icon={faBadgeCheck} /> : item.value}
                    </Typography>
                    <Stack direction="column" flexWrap="nowrap" height="100%" width="100%" paddingRight={1}>
                        <Typography fontSize="14px" fontWeight="bold" component="div" textTransform="uppercase" color="inherit">
                            {item.title}
                        </Typography>
                        <Typography fontSize="12px" component="div" color="inherit" lineHeight={1.1}>
                            {item.subtitle1}
                        </Typography>
                        {item.subtitle2 &&
                            <Typography fontSize="12px" component="div" color="muted.main" lineHeight={1.1} sx={{ mt: 1 }}>
                                {item.subtitle2}
                            </Typography>
                        }
                    </Stack>
                </Stack>
            </Box>
        </IndicatorCardAction>
    );
};

///////////////////////////////////////////////////////////////////////////////

export function IndicatorDtoToCardRibbon(items: IndicatorCardDto[] | undefined): React.ReactNode[] {
    return (items ?? []).map((item) => <IndicatorCard key={item.uniqueId} {...item} />);
}
